import { ArrowRight, CheckCircle2, Loader2, UserRound } from 'lucide-react'
import { useEffect } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import {
  returnTargetFrom,
  safeReturnTarget,
  signInUrlReturningTo,
} from '@/lib/return-target'
import { withLanguagePath } from '@/lib/url-language-utils'
import { TournamentModuleNavigation } from './module-navigation'
import { tournamentSummary } from './summary'
import {
  tournamentDateRange,
  tournamentPhaseKeys,
  tournamentUrl,
  useTournamentEdition,
} from './edition'
import { TournamentEmailEligibility } from './email-eligibility'
import { TournamentParticipation } from './participation'
import { TournamentRegistration } from './registration'
import { TournamentRulesViewer } from './rules-viewer'

const panel
  = 'min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6'

export function TournamentPage() {
  const { editionId } = useParams<{ editionId: string }>()
  const { t, language } = useI18n()
  const { actor, authResolved } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { edition, editionQuery, viewer, viewerQuery, lifecycle }
    = useTournamentEdition(editionId)
  const rulesOpen = new URLSearchParams(location.search).get('view') === 'rules'
  const registered = authResolved && !!viewer?.registration
  const participantView
    = registered && !!(lifecycle?.competitionOpen || lifecycle?.ended)
  const refresh = async () => {
    await Promise.all([editionQuery.refetch(), viewerQuery.refetch()])
  }
  useEffect(() => {
    if (!editionId && edition) {
      navigate(
        `${tournamentUrl(edition.id)}${location.search}${location.hash}`,
        { replace: true },
      )
    }
  }, [editionId, edition, location.search, location.hash, navigate])
  useEffect(() => {
    if (
      edition
      && !editionQuery.isFetching
      && edition.contentLanguage !== language
    ) {
      window.location.replace(
        `${window.location.origin}${withLanguagePath(window.location.pathname, edition.contentLanguage)}${window.location.search}${window.location.hash}`,
      )
    }
  }, [edition, editionQuery.isFetching, language])
  const setRules = (open: boolean) => {
    if (!edition)
      return
    const search = new URLSearchParams(location.search)
    if (open) {
      search.set('view', 'rules')
    }
    else {
      search.delete('view')
      search.delete('tournamentSection')
    }
    navigate({
      pathname: tournamentUrl(edition.id),
      search: search.size ? `?${search}` : '',
      hash: location.hash,
    })
  }
  const phaseKey = lifecycle ? tournamentPhaseKeys[lifecycle.phase] : undefined
  const summary = edition ? tournamentSummary(edition, language, t) : []
  const backFromRules = () => {
    const requested = new URLSearchParams(location.search).get('returnTo')
    const target = safeReturnTarget(requested, '')
    if (
      target
      && new URL(target, window.location.origin).pathname.endsWith(
        '/tournaments/hall',
      )
    ) {
      navigate(target)
    }
    else {
      setRules(false)
    }
  }
  return (
    <main
      data-testid="tournament-page"
      data-edition-id={edition?.id}
      data-village-page="tournament-page"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      className="min-h-dvh bg-section-dark px-4 pb-12 pt-24 text-foreground sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <TournamentModuleNavigation
          active="tournament"
          tournamentPath={`${location.pathname}${location.search}${location.hash}`}
        />
        {editionQuery.isPending && (
          <p role="status" className="flex items-center gap-2 py-8">
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
            {t('forum.loading')}
          </p>
        )}
        {editionQuery.isError && (
          <div role="alert" className={panel}>
            <p>
              {editionQuery.error.data?.code === 'NOT_FOUND'
                ? '404'
                : t('forum.errorGeneric')}
            </p>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => {
                void editionQuery.refetch()
              }}
            >
              {t('social.retry')}
            </Button>
          </div>
        )}
        {edition && (
          <>
            <header
              className="mb-5 flex flex-wrap items-start justify-between gap-4"
              lang={edition.contentLanguage}
            >
              <div>
                <p className="mb-1 text-sm text-muted-foreground">
                  {edition.content.title}
                </p>
                <h1 className="text-3xl font-bold leading-tight">
                  {edition.content.name}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {edition.content.format}
                </p>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm">
                  {[
                    {
                      key: 'registrationDays',
                      days: edition.registrationDays,
                      start: edition.registrationStartsAt,
                      end: edition.registrationEndsAt,
                    },
                    {
                      key: 'competitionDays',
                      days: edition.competitionDays,
                      start: edition.competitionStartsAt,
                      end: edition.competitionEndsAt,
                    },
                  ].map(period => (
                    <div key={period.key}>
                      <p className="text-xs text-muted-foreground">
                        {t(`tournament.${period.key}`, { days: period.days })}
                      </p>
                      <p
                        data-testid={`tournament-${period.key}-dates`}
                        className="mt-1 tabular-nums"
                      >
                        {tournamentDateRange(
                          period.start,
                          period.end,
                          edition.timezone,
                          language,
                        )}
                      </p>
                    </div>
                  ))}
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('tournament.timezone')}
                    </p>
                    <p className="mt-1">{edition.timezone}</p>
                    {edition.datesPlaceholder && (
                      <p className="mt-1">{t('tournament.placeholderState')}</p>
                    )}
                  </div>
                </div>
              </div>
              {phaseKey && (
                <span
                  data-testid="tournament-phase"
                  role="status"
                  className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-semibold"
                >
                  {t(`tournament.${phaseKey}`)}
                </span>
              )}
            </header>
            {rulesOpen ? (
              <section className={panel}>
                <h2 className="mb-3 text-xl font-semibold">
                  {t('tournamentRules.title')}
                </h2>
                <TournamentRulesViewer
                  editionId={edition.id}
                  onBack={backFromRules}
                />
              </section>
            ) : participantView ? (
              <TournamentParticipation
                key={`${edition.id}:${actor?.id ?? ''}`}
                edition={edition}
                onRules={() => setRules(true)}
              />
            ) : (
              <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                <aside
                  className={`${panel} order-last lg:order-none lg:col-start-2 lg:row-start-1`}
                  data-testid="tournament-access"
                >
                  <h2 className="text-xl font-semibold">
                    {t('tournament.register')}
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t('tournament.emailCondition')}
                  </p>
                  {viewerQuery.isPending && (
                    <p className="mt-4" role="status">
                      {t('forum.loading')}
                    </p>
                  )}
                  {viewerQuery.isError && (
                    <div className="mt-4" role="alert">
                      <p>{t('forum.errorGeneric')}</p>
                      <Button
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                          void viewerQuery.refetch()
                        }}
                      >
                        {t('social.retry')}
                      </Button>
                    </div>
                  )}
                  {viewer && lifecycle && (
                    <>
                      {lifecycle.registrationClosed && !registered ? (
                        <div className="mt-4 space-y-2">
                          <h3 className="font-semibold">
                            {t('tournament.closedTitle')}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {t('tournament.closedBody')}
                          </p>
                          {lifecycle.ended && !edition.resultsPublished && (
                            <p className="text-sm">
                              {t('tournament.resultsPending')}
                            </p>
                          )}
                        </div>
                      ) : !viewer.registeredAccount ? (
                        <div className="mt-4 space-y-3">
                          <UserRound className="size-6" aria-hidden="true" />
                          <h3 className="font-semibold">
                            {t('tournament.loginTitle')}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {t('tournament.loginBody')}
                          </p>
                          <Button asChild className="min-h-11 w-full">
                            <Link
                              data-testid="tournament-signin"
                              to={signInUrlReturningTo(
                                returnTargetFrom(location),
                              )}
                            >
                              {t('nav.signIn')}
                            </Link>
                          </Button>
                        </div>
                      ) : (
                        <>
                          {!viewer.registration && (
                            <TournamentEmailEligibility
                              status={viewer.emailEligibility}
                              refresh={refresh}
                            />
                          )}
                          <TournamentRegistration
                            edition={edition}
                            viewer={viewer}
                            refresh={refresh}
                          />
                        </>
                      )}
                    </>
                  )}
                  <p className="mt-4 border-t border-border pt-4 text-[13px] text-muted-foreground">
                    {t('tournament.quotaIndependence')}
                  </p>
                </aside>
                <section className={`${panel} lg:col-start-1 lg:row-start-1`}>
                  <h2 className="mb-5 text-xl font-semibold">
                    {t('tournamentRules.eventRules')}
                  </h2>
                  <ul
                    data-testid="tournament-summary"
                    className="space-y-[18px] text-[15px] leading-relaxed"
                  >
                    {summary.map(line => (
                      <li key={line} className="flex items-start gap-3">
                        <CheckCircle2
                          className="mt-1 size-[17px] shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="min-w-0 [overflow-wrap:anywhere]">
                          {line}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    data-testid="tournament-rules"
                    onClick={() => setRules(true)}
                    className="mt-[18px] inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t('tournament.rules')}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </button>
                </section>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  )
}
